import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ConfigurationLoaderProps, ConfigurationLoaderReturn } from '../types';

export function useConfigurationLoader({
  setColumnVisibilityModel,
  setColumnOrder,
  hasLoadedFromSavedRef,
  dispatch,
  onSuccess,
  onError,
}: ConfigurationLoaderProps): ConfigurationLoaderReturn {
  const location = useLocation();
  const [isLoadingConfiguration, setIsLoadingConfiguration] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const loadColumnsId = urlParams.get('loadColumns');
    const loadCompleteId = urlParams.get('loadComplete');

    const loadConfigurationFromDynamoDB = async (configId: string, isComplete: boolean) => {
      setIsLoadingConfiguration(true);
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const { getFilterById } = await import('../../../../../../services/savedFiltersService');
        const configData = await getFilterById(configId);

        if (configData) {
          if (configData.columnVisibilityModel) {
            hasLoadedFromSavedRef.current = true;
            setColumnVisibilityModel(configData.columnVisibilityModel);
          }
          if (configData.columnOrder) {
            setColumnOrder(configData.columnOrder);
          }

          onSuccess(
            `${isComplete ? 'Complete search' : 'Column configuration'} "${configData.name}" applied successfully`,
          );

          urlParams.delete(isComplete ? 'loadComplete' : 'loadColumns');
          const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
          window.history.replaceState({}, '', newUrl);
        } else {
          onError(`${isComplete ? 'Complete search' : 'Column configuration'} not found`);
        }
      } catch (error) {
        console.error(`Error loading ${isComplete ? 'complete search' : 'column configuration'}:`, error);
        onError(`Error loading ${isComplete ? 'complete search' : 'column configuration'}`);
      } finally {
        setIsLoadingConfiguration(false);
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    const loadFromLocalStorage = (key: string, type: 'complete' | 'columns') => {
      try {
        const data = localStorage.getItem(key);
        if (data) {
          const parsed = JSON.parse(data);

          if (parsed.columnVisibilityModel) {
            hasLoadedFromSavedRef.current = true;
            setColumnVisibilityModel(parsed.columnVisibilityModel);
          }
          if (parsed.columnOrder) {
            setColumnOrder(parsed.columnOrder);
          }

          onSuccess(
            `${type === 'complete' ? 'Complete search' : 'Column configuration'} "${parsed.name}" applied successfully`,
          );

          urlParams.delete(type === 'complete' ? 'loadComplete' : 'loadColumns');
          const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
          window.history.replaceState({}, '', newUrl);
        } else {
          onError(`${type === 'complete' ? 'Complete search' : 'Column configuration'} not found`);
        }
      } catch (error) {
        console.error(`Error loading legacy ${type} configuration:`, error);
        onError(`Error loading ${type} configuration`);
      }
    };

    if (loadCompleteId) {
      if (loadCompleteId.startsWith('completeSearch_')) {
        loadFromLocalStorage(loadCompleteId, 'complete');
      } else {
        loadConfigurationFromDynamoDB(loadCompleteId, true);
      }
    } else if (loadColumnsId) {
      if (loadColumnsId.startsWith('columnSelection_')) {
        loadFromLocalStorage(loadColumnsId, 'columns');
      } else {
        loadConfigurationFromDynamoDB(loadColumnsId, false);
      }
    }
  }, [
    location.search,
    setColumnVisibilityModel,
    setColumnOrder,
    dispatch,
    hasLoadedFromSavedRef,
    onSuccess,
    onError,
  ]);

  return { isLoadingConfiguration };
}
